import map from 'lodash/map';
import { useCallback } from 'react';
import { EMPTY_OBJECT } from '../../../../consts/empty';
import { useParams } from '../../../../navigation';
import NavigationGroup from '../../components/NavigationGroup';
import NavigationItem from '../../components/NavigationItem';
import NavigationSubItem from '../../components/NavigationSubItem';
import { NavigationGroup as PlatformNavigationGroup } from '../../components/platform/NavigationGroup';
import { isPlatformSideNav } from '../../helpers';
import type {
  NavItem,
  NavigationItemSlotPropsType,
  NavigationItem as NavigationItemType,
} from '../../types';
import { getIsNavGroupActive } from '../../utils';
import { ActionNavigationItem } from './ActionNavigationItem';
import { getActiveNavItem, getIsPathActive } from './helpers';
import { UserNavigationItem } from './UserNavigationItem';

type NavItemComponentProps = {
  activePathnames: string[];
  activeMenuItemId?: string | null;
  item: NavigationItemType;
  setActiveMenuItemId: (id: string | null) => void;
  slotProps?: NavigationItemSlotPropsType;
};

export default function NavigationItemComponent(props: NavItemComponentProps) {
  const { activePathnames, activeMenuItemId, item, setActiveMenuItemId, slotProps } = props;

  const {
    navigationItem: { labelVariant },
  } = slotProps ?? (EMPTY_OBJECT as NavigationItemSlotPropsType);
  const actionprops =
    item.type === 'ACTION' && 'actionProps' in item ? item.actionProps : undefined;
  const { projectSlug } = useParams<{ projectSlug?: string }>();

  const onOpenChange = useCallback(
    (id: string, open: boolean) => {
      setActiveMenuItemId(open ? id : null);
    },
    [setActiveMenuItemId],
  );

  const handleClick = useCallback((e: React.MouseEvent, navItem: NavigationItemType) => {
    if ('isInvalidUrl' in navItem && navItem.isInvalidUrl) {
      e.preventDefault();
      e.stopPropagation();
      console.error('Dynamic Params not passed properly', navItem);
    }
  }, []);

  if (item.type === 'GROUP') {
    const activeNavItem = getActiveNavItem({
      navItems: item.items,
      activePathnames,
    });

    const isGroupActive = getIsNavGroupActive({
      item,
      activeMenuItemId,
      activePathnames,
      projectSlug,
    });

    if (isPlatformSideNav(labelVariant)) {
      return (
        <PlatformNavigationGroup
          activeNavItem={activeNavItem}
          isGroupActive={isGroupActive}
          item={item}
          slotProps={slotProps}
        />
      );
    }

    return (
      <NavigationGroup
        activeIconName={item.activeIcon}
        iconName={item.icon}
        isActive={isGroupActive}
        name={item.name}
        slotProps={slotProps}
        variant="rich"
      >
        {/* safe to assume that the items are all URL items for now
         since we do not have support for 2-level or more nested Nav items
        */}
        {map(item.items, (subItem: NavItem) => {
          const isSubItemActive = subItem.id === activeNavItem?.id;
          return (
            <NavigationSubItem
              id={subItem.id}
              isActive={isSubItemActive}
              key={subItem.id}
              name={subItem.name}
              onClick={(e) => handleClick(e, subItem)}
              slotProps={slotProps}
              url={subItem.readonly ? '#' : subItem.url}
              variant="rich"
            />
          );
        })}
      </NavigationGroup>
    );
  }

  if (item.type === 'USER_MENU') {
    return (
      <UserNavigationItem
        activePathnames={activePathnames}
        navItem={item}
        onOpenChange={onOpenChange}
        open={activeMenuItemId === item.id}
        slotProps={slotProps}
      />
    );
  }

  if (item.type === 'ACTION') {
    const { disabled, active } = item.activeDisabledControllers ?? {};
    const isActive = active ?? activeMenuItemId === item.id;

    return (
      <ActionNavigationItem
        action={item.action}
        actionProps={actionprops}
        activeEndIcon={item.activeEndIcon}
        activeIcon={item.activeIcon}
        disabled={disabled}
        endIcon={item.endIcon}
        icon={item.icon}
        iconClassName={item.iconClassName}
        id={item.id}
        isActive={isActive}
        name={item.name}
        onClick={item.onClick}
        onOpenChange={onOpenChange}
        shortcutKeys={item.shortcutKeys}
        slotProps={slotProps}
        variant="rich"
      />
    );
  }

  if (
    item.type === 'LABEL' ||
    item.type === 'CUSTOM' ||
    item.type === 'BUTTON' ||
    item.type === 'COLOR_SCHEME_SWITCHER' ||
    item.type === 'LANGUAGE_SWITCHER'
  ) {
    return null; // labels are not rendered in the SideNav, only in LiteSideNavigation, will update if usage comes up
  }

  const isActive =
    (!activeMenuItemId || activeMenuItemId === item.id) &&
    getIsPathActive({ activePathnames, path: item.url });

  return (
    <NavigationItem
      activeEndIcon={item.activeEndIcon}
      activeIcon={item.activeIcon}
      endIcon={item.endIcon}
      icon={item.icon}
      id={item.id}
      isActive={isActive}
      name={item.name}
      navigationOptions={item.navigationOptions}
      onClick={(e) => handleClick(e, item)}
      openInNewTab={item.openInNewTab}
      slotProps={slotProps}
      url={item.readonly ? '#' : item.url}
      variant="rich"
    />
  );
}
